/**
 * Page 5: Refine your feed.
 *
 * Reached from the header, both ends of the feed, and the emails' refine link.
 * Topics are the only thing a subscriber can change, which is why there is no
 * cadence or email control here: those are admin-owned and offering them would
 * be a second control for a behaviour this page cannot actually alter.
 */

import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import TrialShell from './TrialShell';
import TrialExpired from './TrialExpired';
import trialApi from './trialApi';
import { useFocusHeading, useTrialData } from './useTrialPage';
import { EVENTS, track } from '../../analytics/campaignAnalytics';

export default function RefinePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { data, error, loading } = useTrialData(
    (cfg) => trialApi.getPreferences(token, cfg),
    [token],
  );

  const [selected, setSelected] = useState([]);
  const [available, setAvailable] = useState([]);
  const [freeText, setFreeText] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const heading = useFocusHeading(Boolean(data));

  useEffect(() => {
    if (!data) return;
    setSelected(data.selected || []);
    setAvailable(data.available || []);
    setFreeText(data.freeText || '');
  }, [data]);

  if (error?.code === 'TRIAL_LINK_EXPIRED' || error?.code === 'TRIAL_LINK_INVALID') {
    return <TrialExpired code={error.code} />;
  }

  const remove = (topic) => {
    setSelected((s) => s.filter((t) => t !== topic));
    setAvailable((a) => (a.includes(topic) ? a : [...a, topic]));
    setSaveError('');
  };

  const add = (topic) => {
    setSelected((s) => (s.includes(topic) ? s : [...s, topic]));
    setAvailable((a) => a.filter((t) => t !== topic));
    setSaveError('');
  };

  const save = async () => {
    setSaving(true);
    setSaveError('');
    try {
      const res = await trialApi.savePreferences(token, selected, freeText);
      track(EVENTS.PREFERENCES_SAVED, {
        topic_count: res.topics.length,
        has_text: Boolean(res.freeText),
        refines_used: res.refinesUsedToday,
      });
      navigate(`/trial/feed/${token}/saved`);
    } catch (err) {
      // Inline, next to what failed. The handoff: errors are written beside the
      // control, never in a toast.
      setSaveError(err.message || 'We could not save your preferences.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <TrialShell
      token={token}
      active="refine"
      onContactClick={() => track(EVENTS.CONTACT_CLICKED)}
      onSiteClick={() => track(EVENTS.SITE_LINK_CLICKED)}
    >
      <main className="tr-main">
        <div className="tr-wrap tr-wrap--refine">
          <h1 className="tr-h1" tabIndex={-1} ref={heading}>Refine your news feed</h1>
          <p className="tr-lede">Help us find more of the stories that matter to you.</p>

          {loading && <p className="tr-body" role="status" style={{ marginTop: 24 }}>Loading your topics</p>}
          {error && !loading && <p className="tr-error" role="alert" style={{ marginTop: 24 }}>{error.message}</p>}

          {data && (
            <>
              <section className="tr-panel" style={{ marginTop: 28 }}>
                <h2 className="tr-h2">Your topics</h2>
                <p className="tr-body" style={{ marginTop: 8 }}>Select the topics you want to follow.</p>

                <p className="tr-overline" style={{ marginTop: 20 }}>{`Following · ${selected.length}`}</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
                  {selected.length === 0 && <span className="tr-meta">No topics selected yet.</span>}
                  {selected.map((t) => (
                    <button
                      key={t} type="button" className="tr-chip" aria-pressed="true"
                      aria-label={`Remove ${t}`} onClick={() => remove(t)}
                    >
                      {t} <span aria-hidden="true">&times;</span>
                    </button>
                  ))}
                </div>

                {available.length > 0 && (
                  <>
                    <p className="tr-overline" style={{ marginTop: 24 }}>Add topics</p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
                      {available.map((t) => (
                        <button
                          key={t} type="button" className="tr-chip" aria-pressed="false"
                          aria-label={`Add ${t}`} onClick={() => add(t)}
                        >
                          {t} <span aria-hidden="true">+</span>
                        </button>
                      ))}
                    </div>
                  </>
                )}

                {/*
                  * Adi, 29 October 2026: "a place for verbal text that details
                  * what the client is looking for."
                  *
                  * The objection that kept this out before was about unvalidated
                  * CHIPS reaching a prompt. This is prose, and it goes into the
                  * same `brief` field the landing form already accepts, which is
                  * the one that actually drives curation.
                  */}
                {data.supportsCustomTopics && (
                  <>
                    <label className="tr-overline" htmlFor="tr-refine-text" style={{ display: 'block', marginTop: 24 }}>
                      Or tell us in your own words
                    </label>
                    <p className="tr-meta" style={{ marginTop: 6 }}>
                      Describe what you are looking for. This shapes your next list.
                    </p>
                    <textarea
                      id="tr-refine-text"
                      className="tr-input tr-textarea"
                      rows={4}
                      value={freeText}
                      maxLength={2000}
                      onChange={(e) => { setFreeText(e.target.value); setSaveError(''); }}
                      style={{ marginTop: 8, width: '100%' }}
                    />
                  </>
                )}
              </section>

              {/* The budget, stated before they spend it rather than after. */}
              {typeof data.refineLimit === 'number' && (
                <p className="tr-meta" style={{ marginTop: 12 }}>
                  {`${Math.max(0, data.refineLimit - (data.refinesUsedToday || 0))} of ${data.refineLimit} refines left today.`}
                </p>
              )}

              {saveError && <p className="tr-error" role="alert" style={{ marginTop: 16 }}>{saveError}</p>}

              <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginTop: 24, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="tr-btn tr-btn--primary tr-btn--lg tr-btn--block-mobile"
                  onClick={save}
                  disabled={saving}
                >
                  {saving ? 'Building your new list…' : 'Save and get a new list'}
                </button>
                <Link className="tr-link" to={`/trial/feed/${token}`}>Cancel</Link>
              </div>
            </>
          )}
        </div>
      </main>
    </TrialShell>
  );
}
